import { useTranslation } from 'react-i18next';
import { generatePath, Link, useLocation } from 'react-router-dom';
import {
  buildQueryFilters,
  DEFAULT_PAGE,
  MAXIMUM_PAGINATION,
  useGetChildOrganizations,
  useGetOrganization,
} from '@sib/api';
import { useUserSetupStore } from '@sib/auth';
import { cn, buttonVariants, Button } from '@sib/ui';
import { Calendar } from '@sib/ui/icons';
import {
  Account,
  Billing,
  Chart,
  Chat,
  Dashboard,
  Documents,
  Info,
  TeamOrg,
  Savings,
  Feedback,
  LocationList,
  Vendors,
  YourTickets,
  QuestionMark,
  BillPay,
  ContractTerms,
} from '@sib/ui/icons';
import { MEDIA_SM_QUERY } from '@spendbrain/config/constants';
import { paths } from '@spendbrain/config/paths';
import { useMediaQuery, usePowerBiReports } from '@spendbrain/hooks';
import { useFeedbackStore, useSidebarStore } from '../stores';

const useSidebarItems = () => {
  const { t } = useTranslation();
  const { openFeedbackDialog } = useFeedbackStore();
  const { powerBiReports } = usePowerBiReports();
  const showReportsDirectory = powerBiReports.length > 0;
  const {
    organization: { id: organizationId },
    isSibEmployee,
  } = useUserSetupStore();

  const ticketsPath = isSibEmployee
    ? generatePath(paths.tickets.employee.root, { orgId: organizationId })
    : paths.tickets.client.root;
  const { data: organizationData } = useGetOrganization(organizationId);
  const hasVectorClientId = !!organizationData?.vectorClientId;
  const hasNetsuiteCustomerId = !!organizationData?.netsuiteCustomerId;
  const hasBillingAccess = hasVectorClientId || hasNetsuiteCustomerId;

  const { data: childOrgsData } = useGetChildOrganizations(
    organizationId || '',
    {
      page: DEFAULT_PAGE,
      size: MAXIMUM_PAGINATION,
      filter: buildQueryFilters([
        { field: 'micrositesEnabled', operator: 'eq', value: true },
      ]),
    }
  );

  const hasChildren = (childOrgsData?.totalItems ?? 0) > 0;

  return [
    ...(hasChildren
      ? [
          {
            href: paths.subsidiaryOrganization,
            title: t('sidebar.subsidiaryOrganization'),
            icon: TeamOrg,
          },
        ]
      : []),
    {
      href: paths.dashboard,
      title: t('sidebar.dashboard'),
      icon: Dashboard,
    },
    {
      href: paths.savings,
      title: t('sidebar.savingsAndSpendAnalysis'),
      icon: Savings,
    },
    {
      href: paths.services,
      title: t('sidebar.services'),
      icon: Chart,
    },
    {
      href: paths.documents.root,
      title: t('sidebar.documents'),
      icon: Documents,
    },
    {
      href: paths.locations,
      title: t('sidebar.locations'),
      icon: LocationList,
    },

    ...(showReportsDirectory
      ? [{ href: paths.reports, title: t('sidebar.reports'), icon: Chart }]
      : []),
    {
      href: ticketsPath,
      title: t('sidebar.tickets'),
      icon: YourTickets,
    },
    {
      href: paths.upcomingEvents,
      title: t('sidebar.upcomingEvents'),
      icon: Calendar,
    },
    {
      href: paths.chat,
      title: t('sidebar.chat'),
      icon: Chat,
    },
    {
      href: paths.yourTeam,
      title: t('sidebar.yourTeam'),
      icon: TeamOrg,
    },
    {
      href: paths.account,
      title: t('sidebar.account'),
      icon: Account,
    },
    ...(hasBillingAccess
      ? [
          {
            href: paths.billing,
            title: t('sidebar.billings'),
            icon: Billing,
          },
        ]
      : []),
    {
      href: paths.vendors,
      title: t('sidebar.vendors'),
      icon: Vendors,
    },
    {
      href: paths.billPay,
      title: t('sidebar.billPay'),
      icon: BillPay,
    },
    {
      href: paths.contractTerms,
      title: t('sidebar.contractTerms'),
      icon: ContractTerms,
    },
    {
      href: paths.help,
      title: t('sidebar.help'),
      icon: QuestionMark,
    },
    {
      href: 'https://aboutsib.com/',
      title: t('sidebar.about'),
      icon: Info,
    },
    {
      title: t('sidebar.sendFeedback'),
      icon: Feedback,
      onClick: openFeedbackDialog,
    },
  ];
};

export const NavigationMenu = () => {
  const { pathname } = useLocation();
  const { setSidebarOpen } = useSidebarStore();
  const sidebarItems = useSidebarItems();
  const currentPathname = String(pathname);
  const isMobile = useMediaQuery(MEDIA_SM_QUERY);

  return (
    <div className="group flex flex-col justify-between gap-4">
      <nav className="grid gap-1">
        {(sidebarItems || []).map(({ href, title, icon: Icon, onClick }) => {
          const isActive = currentPathname.startsWith(href?.toString() ?? '/#');
          const className = cn(
            buttonVariants({
              variant: 'ghost',
            }),
            'text-primary justify-start rounded-none px-[18px] font-normal no-underline',
            isActive
              ? 'bg-secondary hover:text-primary font-medium hover:brightness-100'
              : 'hover:bg-muted hover:brightness-100'
          );
          const isExternal = href?.startsWith('http');

          return onClick ? (
            <Button
              variant="ghost"
              className={className}
              key={title}
              onClick={onClick}
            >
              <Icon className="size-[18px]" />
              {title}
            </Button>
          ) : (
            <Link
              key={href}
              to={href?.toString() ?? '/#'}
              title={title}
              onClick={onClick}
              target={isExternal ? '_blank' : undefined}
              rel={isExternal ? 'noopener noreferrer' : undefined}
              className={className}
              {...(isMobile && {
                onClick: () => setSidebarOpen(false),
              })}
            >
              <Icon className="size-[18px]" />
              {title}
            </Link>
          );
        })}
      </nav>
    </div>
  );
};

NavigationMenu.displayName = 'NavigationMenu';
